import { useEffect, useState } from "react";
import api, { formatApiError, formatDate, formatMoney } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["paid", "shipped", "delivered", "cancelled"];
const CARRIERS = ["UPS", "FedEx", "USPS", "Freight", "Other"];

function ShopSettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/shop/settings").then(({ data }) => setS(data)); }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/shop/settings", { ...s, flat_shipping_rate: parseFloat(s.flat_shipping_rate) || 0 });
      setS(data); toast.success("Shop settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!s) return null;
  return (
    <div className="surface p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="shop-settings-panel">
      <label className="block">
        <span className="font-mono text-[10px] text-[#9CA3AF] tracking-widest uppercase">Flat shipping rate ($)</span>
        <input type="number" step="0.01" min="0" value={s.flat_shipping_rate} onChange={(e) => setS({ ...s, flat_shipping_rate: e.target.value })} className="input-rugged mt-1" data-testid="shop-settings-shipping-rate" />
      </label>
      <label className="block">
        <span className="font-mono text-[10px] text-[#9CA3AF] tracking-widest uppercase">Free pickup option label</span>
        <input value={s.pickup_label} onChange={(e) => setS({ ...s, pickup_label: e.target.value })} className="input-rugged mt-1" data-testid="shop-settings-pickup-label" />
      </label>
      <label className="flex items-center gap-2 input-rugged cursor-pointer">
        <input type="checkbox" checked={!!s.tax_enabled} onChange={(e) => setS({ ...s, tax_enabled: e.target.checked })} data-testid="shop-settings-tax" />
        <span className="font-plex text-sm">Collect sales tax (Stripe Tax)</span>
      </label>
      <button onClick={save} className="btn-primary justify-center" data-testid="shop-settings-save">Save settings</button>
    </div>
  );
}

function OrderRow({ o, onSaved }) {
  const [tracking, setTracking] = useState(o.tracking_number || "");
  const [carrier, setCarrier] = useState(o.carrier || "UPS");
  const a = o.shipping_address?.address || {};
  const update = async (body, msg) => {
    try { await api.patch(`/admin/shop/orders/${o.id}`, body); toast.success(msg); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const markShipped = () => update({ status: "shipped", tracking_number: tracking, carrier }, "Marked shipped · customer emailed");
  return (
    <div className="surface p-4" data-testid={`admin-shop-order-${o.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-chivo font-bold text-white">#{o.id.slice(-6).toUpperCase()}</span>
            <span className={`tag ${o.status === "paid" ? "tag-active" : ""}`} data-testid={`admin-shop-order-status-${o.id}`}>{o.status.toUpperCase()}</span>
            <span className="tag">{o.guest_checkout === false ? "LOGGED IN" : o.user_id ? "GUEST · ACCOUNT CREATED" : "GUEST"}</span>
            <span className="tag">{o.price_tier === "member" ? "MEMBER PRICE" : "MSRP"}</span>
          </div>
          <div className="font-plex text-sm text-white mt-2">{o.product_name} × {o.quantity} · <strong className="text-[#00D4FF]">{formatMoney(o.amount_total)}</strong>
            <span className="font-mono text-xs text-[#52525B]"> (ship {formatMoney(o.amount_shipping)} · tax {formatMoney(o.amount_tax)})</span></div>
          <div className="font-mono text-xs text-[#9CA3AF] mt-1">{formatDate(o.paid_at || o.created_at)} · {o.customer_name} · {o.customer_email} · {o.customer_phone || "—"}</div>
          <div className="font-mono text-xs text-[#52525B] mt-1">{o.shipping_method || "—"} → {[a.line1, a.line2, a.city, a.state, a.postal_code].filter(Boolean).join(", ") || "no address"}</div>
          {o.tracking_number && <div className="font-mono text-xs text-[#9CA3AF] mt-1">Tracking: {o.carrier} {o.tracking_number}</div>}
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:max-w-md">
          {o.status === "paid" && (
            <>
              <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className="input-rugged w-28" data-testid={`admin-shop-order-carrier-${o.id}`}>
                {CARRIERS.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking #" className="input-rugged w-44" data-testid={`admin-shop-order-tracking-${o.id}`} />
              <button onClick={markShipped} className="btn-primary text-xs" data-testid={`admin-shop-order-ship-${o.id}`}>Mark shipped</button>
            </>
          )}
          <select value={o.status} onChange={(e) => update({ status: e.target.value }, "Status updated")} className="input-rugged w-36" data-testid={`admin-shop-order-status-select-${o.id}`}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

export function ShopOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await api.get("/admin/shop/orders"); setOrders(data); }
    catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-shop-orders">
      <div className="section-label mb-3">— SHOP SETTINGS</div>
      <ShopSettingsPanel />
      <div className="section-label mb-3">— PAID ORDERS</div>
      {loading ? <div className="font-mono text-xs text-[#52525B]">[LOADING]</div> : (
        <div className="space-y-2">
          {orders.length === 0 && <div className="surface p-6 text-center font-mono text-xs text-[#52525B]" data-testid="admin-shop-orders-empty">No paid orders yet.</div>}
          {orders.map((o) => <OrderRow key={o.id} o={o} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
